/**
 * Canvas Resolver
 *
 * Resolves the final canvas configuration taking into account:
 * - Base screen canvas
 * - Device overrides (mobile/desktop)
 * - Visual search mode
 *
 * Structure:
 * config.search.views[view].screens[screen].canvas - base canvas
 * config.search.views[view].device[device][screen].canvas - device override
 *
 * Priority (highest to lowest):
 * 1. Device override canvas (if exists)
 * 2. Base screen canvas
 */
class CanvasResolver {
  static resolveV2Owner(config, { view, screen, device, visualSearch = false, presentation }) {
    const settings = config?.search?.views?.[view];
    if (!settings) return undefined;
    const candidates = target => [
      ...(visualSearch ? [
        settings.presentations?.[presentation]?.modes?.visual?.[target],
        device === 'mobile' && settings.device?.mobile?.modes?.visual?.[target],
        settings.modes?.visual?.[target],
      ] : []),
      settings.presentations?.[presentation]?.[target],
      device === 'mobile' && settings.device?.mobile?.[target],
      settings.screens?.[target],
    ];
    const screens = screen === 'empty' ? [screen, 'results', 'noResults'] : [screen];
    if (!screens.flatMap(candidates).some(owner => owner?.canvas?.layoutContractVersion === 2)) return undefined;
    const enabled = target => candidates(target).find(owner => owner?.canvas && !CanvasResolver.isDisabledCanvas(owner.canvas));
    return enabled(screen) || (screen === 'empty' ? enabled('results') || enabled('noResults') : null) || null;
  }

  static isDisabledCanvas(canvas) {
    if (!canvas?.nodes || !canvas.root) {
      return false;
    }

    const rootNode = canvas.nodes.find((node) => node.id === canvas.root);
    return rootNode?.props?.disabled === true;
  }

  /**
   * Resolve canvas for given context
   *
   * @param {Object} config - Full search experience configuration
   * @param {Object} context - Resolution context
   * @param {string} context.view - View name (e.g., 'searchBox', 'dropdown')
   * @param {string} context.screen - Screen name (e.g., 'results', 'empty', 'noResults')
   * @param {string} context.device - Device type ('desktop' or 'mobile')
   * @param {boolean} [context.visualSearch=false] - Whether visual search is active
   * @returns {Object|null} Resolved canvas or null if not found
   */
  static resolve(config, context) {
    if (!config || !context) {
      return null;
    }

    const v2Owner = CanvasResolver.resolveV2Owner(config, context);
    if (v2Owner !== undefined) return v2Owner?.canvas || null;
    const { view, screen, device, visualSearch = false } = context;

    if (!view || !screen) {
      // console.warn('[CanvasResolver] View and screen are required');
      return null;
    }

    const viewConfig = config?.search?.views?.[view];
    if (!viewConfig) {
      // console.warn(`[CanvasResolver] View '${view}' not found`);
      return null;
    }

    const presentationCanvas = !visualSearch
      && viewConfig.presentations?.[context.presentation]?.[screen]?.canvas;
    if (presentationCanvas && !CanvasResolver.isDisabledCanvas(presentationCanvas)) {
      return presentationCanvas;
    }

    const screenConfig = viewConfig.screens?.[screen];
    if (!screenConfig) {
      // console.warn(`[CanvasResolver] Screen '${screen}' not found in view '${view}'`);
      return null;
    }

    // Base canvas
    const baseCanvas = screenConfig.canvas;
    if (!baseCanvas) {
      // console.warn(`[CanvasResolver] No canvas found for ${view}/${screen}`);
      return null;
    }

    // Device override (if specified)
    let canvas = baseCanvas;
    if (device) {
      const deviceOverride = viewConfig.device?.[device]?.[screen]?.canvas;

      if (deviceOverride && !CanvasResolver.isDisabledCanvas(deviceOverride)) {
        // console.debug(`[CanvasResolver] ✅ Using device override for ${device} on ${view}/${screen}`);
        canvas = deviceOverride;
      } else {
        // console.debug(`[CanvasResolver] ⚠️ No device override for ${device} on ${view}/${screen}, using base canvas`);
      }
    }

    // Visual search override (if active)
    if (visualSearch) {
      const visualOverride = viewConfig.modes?.visual?.[screen]?.canvas;
      if (visualOverride) {
        // console.debug(`[CanvasResolver] 📷 Using visual search override for ${view}/${screen}`);
        canvas = visualOverride;
      } else {
        // console.debug('[CanvasResolver] Visual search mode active but no override found');
      }
    }

    return canvas;
  }

  /**
   * Check if device override exists
   *
   * @param {Object} config
   * @param {Object} context
   * @returns {boolean}
   */
  static hasDeviceOverride(config, context) {
    const { view, screen, device } = context;
    return !!config?.search?.views?.[view]?.device?.[device]?.[screen]?.canvas;
  }

  /**
   * Get all available screens for a view
   *
   * @param {Object} config
   * @param {string} view
   * @returns {string[]}
   */
  static getAvailableScreens(config, view) {
    const screens = config?.search?.views?.[view]?.screens;
    return screens ? Object.keys(screens) : [];
  }

  /**
   * Get all available devices for a screen
   *
   * @param {Object} config
   * @param {string} view
   * @param {string} screen
   * @returns {string[]}
   */
  static getAvailableDevices(config, view, screen) {
    const devices = config?.search?.views?.[view]?.device;
    if (!devices) {
      return [];
    }

    // Filter devices that have this screen
    return Object.keys(devices).filter(
      (deviceKey) => devices[deviceKey]?.[screen]?.canvas,
    );
  }

  /**
   * Validate canvas structure
   *
   * @param {Object} canvas
   * @returns {Object} { valid: boolean, errors: string[] }
   */
  static validate(canvas) {
    const errors = [];

    if (!canvas) {
      errors.push('Canvas is null or undefined');
      return { valid: false, errors };
    }

    if (!canvas.root) {
      errors.push('Canvas missing root node ID');
    }

    if (!canvas.nodes || !Array.isArray(canvas.nodes)) {
      errors.push('Canvas missing nodes array');
    } else {
      // Validate root node exists
      const rootNode = canvas.nodes.find((n) => n.id === canvas.root);
      if (!rootNode) {
        errors.push(`Root node '${canvas.root}' not found in nodes array`);
      }

      // Validate node IDs are unique
      const ids = canvas.nodes.map((n) => n.id);
      const duplicates = ids.filter((id, index) => ids.indexOf(id) !== index);
      if (duplicates.length > 0) {
        errors.push(`Duplicate node IDs found: ${duplicates.join(', ')}`);
      }

      // Validate slot references
      canvas.nodes.forEach((node) => {
        if (node.slots) {
          Object.entries(node.slots).forEach(([slotName, slotValue]) => {
            if (Array.isArray(slotValue)) {
              slotValue.forEach((refId) => {
                if (!ids.includes(refId)) {
                  errors.push(`Node '${node.id}' slot '${slotName}' references non-existent node '${refId}'`);
                }
              });
            }
          });
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Get nodes by type
   *
   * @param {Object} canvas
   * @param {string} type - Node type (e.g., 'products', 'facets')
   * @returns {Object[]}
   */
  static getNodesByType(canvas, type) {
    if (!canvas?.nodes) {
      return [];
    }
    return canvas.nodes.filter((node) => node.type === type);
  }

  /**
   * Find node by ID
   *
   * @param {Object} canvas
   * @param {string} nodeId
   * @returns {Object|null}
   */
  static findNode(canvas, nodeId) {
    if (!canvas?.nodes) {
      return null;
    }
    return canvas.nodes.find((node) => node.id === nodeId) || null;
  }

  /**
   * Check if node type exists in canvas
   *
   * @param {Object} canvas
   * @param {string} type
   * @returns {boolean}
   */
  static hasNodeType(canvas, type) {
    return this.getNodesByType(canvas, type).length > 0;
  }
}

export default CanvasResolver;
